import * as Phaser from "phaser";
import type { TileChange, UpdateTileLayerConfig } from "@workadventure/map-editor";
import { TilemapEditsManager } from "@workadventure/map-editor";
import type { EditMapCommandMessage } from "@workadventure/messages";
import { get } from "svelte/store";
import type { Subscription } from "rxjs";
import {
    mapEditorSelectedTileIndexStore,
    mapEditorSelectedTileLayerIdStore,
    mapEditorShowGridStore,
    mapEditorTileBrushToolStore,
    mapEditorTilePreviewStore,
} from "../../../../Stores/MapEditorStore";
import { enableUserInputsStore } from "../../../../Stores/UserInputStore";
import { PathTileType } from "../../../../Utils/PathfindingManager";
import { waScaleManager } from "../../../Services/WaScaleManager";
import { DEPTH_MAP_EDITOR_AREAS_INDEX } from "../../DepthIndexes";
import type { GameMapFrontWrapper } from "../../GameMap/GameMapFrontWrapper";
import type { GameScene } from "../../GameScene";
import { CreateTileLayerFrontCommand } from "../Commands/Tilemap/CreateTileLayerFrontCommand";
import { DeleteTileLayerFrontCommand } from "../Commands/Tilemap/DeleteTileLayerFrontCommand";
import { MoveTileLayerFrontCommand } from "../Commands/Tilemap/MoveTileLayerFrontCommand";
import { PutTilesFrontCommand } from "../Commands/Tilemap/PutTilesFrontCommand";
import { ResizeMapFrontCommand } from "../Commands/Tilemap/ResizeMapFrontCommand";
import { UpdateTileLayerFrontCommand } from "../Commands/Tilemap/UpdateTileLayerFrontCommand";
import type { MapEditorModeManager } from "../MapEditorModeManager";
import { MapEditorTool } from "./MapEditorTool";

import Pointer = Phaser.Input.Pointer;
import GameObject = Phaser.GameObjects.GameObject;
import Graphics = Phaser.GameObjects.Graphics;
import Image = Phaser.GameObjects.Image;

export class FloorEditorTool extends MapEditorTool {
    private scene: GameScene;
    private mapEditorModeManager: MapEditorModeManager;

    private active = false;
    private painting = false;
    private eraseMode = false;

    /**
     * Changes accumulated during the current pointer gesture (paint/erase drag),
     * keyed by "x,y". They are coalesced into a single command on pointer up.
     */
    private gestureChanges = new Map<string, TileChange>();

    /**
     * Anchor cell for the rectangle brush, set on pointer down.
     */
    private rectStart: { x: number; y: number } | undefined;

    /**
     * Semi-transparent overlay drawn over collidable cells, so collision is visible while editing.
     */
    private collisionOverlayGraphics!: Graphics;

    /**
     * Grid overlay showing the tile boundaries / map area while editing.
     */
    private gridGraphics!: Graphics;

    /**
     * Local-only preview of the selected tile under the cursor (not persisted).
     */
    private tilePreviewImage!: Image;

    private gameMapChangedSubscription: Subscription | undefined;
    private gridStoreUnsubscriber: (() => void) | undefined;
    private tilePreviewStoreUnsubscriber: (() => void) | undefined;

    private freeCameraUp = false;
    private freeCameraDown = false;
    private freeCameraLeft = false;
    private freeCameraRight = false;

    private freeCameraKeyDownHandler = (event: KeyboardEvent) => {
        if (!get(enableUserInputsStore) || event.ctrlKey || event.metaKey) {
            return;
        }
        const key = event.key;
        if (key === "ArrowUp" || key === "w" || key === "W" || key === "z" || key === "Z") {
            this.freeCameraUp = true;
        } else if (key === "ArrowDown" || key === "s" || key === "S") {
            this.freeCameraDown = true;
        } else if (key === "ArrowLeft" || key === "a" || key === "A" || key === "q" || key === "Q") {
            this.freeCameraLeft = true;
        } else if (key === "ArrowRight" || key === "d" || key === "D") {
            this.freeCameraRight = true;
        }
    };

    private freeCameraKeyUpHandler = (event: KeyboardEvent) => {
        const key = event.key;
        if (key === "ArrowUp" || key === "w" || key === "W" || key === "z" || key === "Z") {
            this.freeCameraUp = false;
        } else if (key === "ArrowDown" || key === "s" || key === "S") {
            this.freeCameraDown = false;
        } else if (key === "ArrowLeft" || key === "a" || key === "A" || key === "q" || key === "Q") {
            this.freeCameraLeft = false;
        } else if (key === "ArrowRight" || key === "d" || key === "D") {
            this.freeCameraRight = false;
        }
        // Forward so keyboard shortcuts (undo/redo, tool switching) still work while controls are disabled.
        this.mapEditorModeManager.handleKeyDownEvent(event);
    };

    private freeCameraWheelHandler = (
        pointer: Pointer,
        gameObjects: GameObject[],
        deltaX: number,
        deltaY: number,
        deltaZ: number,
    ) => {
        this.scene.handleMouseWheel(deltaY);
    };

    private pointerDownEventHandler!: (pointer: Pointer, gameObjects: GameObject[]) => void;
    private pointerMoveEventHandler!: (pointer: Pointer, gameObjects: GameObject[]) => void;
    private pointerUpEventHandler!: (pointer: Pointer, gameObjects: GameObject[]) => void;

    constructor(mapEditorModeManager: MapEditorModeManager) {
        super();
        this.mapEditorModeManager = mapEditorModeManager;
        this.scene = this.mapEditorModeManager.getScene();
        this.collisionOverlayGraphics = this.scene.add
            .graphics()
            .setDepth(DEPTH_MAP_EDITOR_AREAS_INDEX)
            .setVisible(false);
        this.gridGraphics = this.scene.add.graphics().setDepth(DEPTH_MAP_EDITOR_AREAS_INDEX).setVisible(false);
        this.tilePreviewImage = this.scene.add
            .image(0, 0, "")
            .setOrigin(0, 0)
            .setDepth(DEPTH_MAP_EDITOR_AREAS_INDEX)
            .setAlpha(0.6)
            .setVisible(false);

        this.gridStoreUnsubscriber = mapEditorShowGridStore.subscribe(() => {
            if (this.active) {
                this.drawGrid();
            }
        });
        this.tilePreviewStoreUnsubscriber = mapEditorTilePreviewStore.subscribe((enabled) => {
            if (!enabled) {
                this.tilePreviewImage.setVisible(false);
            }
        });
    }

    public update(time: number, dt: number): void {
        if (!this.active || !get(enableUserInputsStore)) {
            return;
        }
        const factorToMove = 10 * (1 / waScaleManager.zoomModifier);
        if (this.freeCameraUp) {
            this.scene.getCameraManager().scrollCamera(0, -factorToMove);
        }
        if (this.freeCameraDown) {
            this.scene.getCameraManager().scrollCamera(0, factorToMove);
        }
        if (this.freeCameraLeft) {
            this.scene.getCameraManager().scrollCamera(-factorToMove, 0);
        }
        if (this.freeCameraRight) {
            this.scene.getCameraManager().scrollCamera(factorToMove, 0);
        }
    }

    public clear(): void {
        this.active = false;
        this.painting = false;
        this.gestureChanges.clear();
        this.rectStart = undefined;
        this.freeCameraUp = false;
        this.freeCameraDown = false;
        this.freeCameraLeft = false;
        this.freeCameraRight = false;
        this.collisionOverlayGraphics.setVisible(false);
        this.gridGraphics.setVisible(false);
        this.tilePreviewImage.setVisible(false);
        this.unbindEventHandlers();
        this.unbindFreeCameraHandlers();
        this.scene.userInputManager.restoreControls("floorEditor");
        this.scene.getCameraManager().startFollowPlayer(this.scene.CurrentPlayer, 1000);
        this.scene.input.setDefaultCursor("auto");
        this.scene.markDirty();
    }

    public activate(): void {
        this.active = true;
        // Free camera (like exploration mode): the camera is not locked on the player.
        this.scene.getCameraManager().setExplorationMode();
        this.scene.userInputManager.disableControls("floorEditor");
        this.scene.input.setDefaultCursor("crosshair");
        this.collisionOverlayGraphics.setVisible(true);
        this.redrawCollisionOverlay();
        this.drawGrid();
        this.bindEventHandlers();
        this.bindFreeCameraHandlers();
        this.scene.markDirty();
    }

    public destroy(): void {
        this.gameMapChangedSubscription?.unsubscribe();
        this.gridStoreUnsubscriber?.();
        this.tilePreviewStoreUnsubscriber?.();
        this.unbindEventHandlers();
        this.unbindFreeCameraHandlers();
        this.scene.userInputManager.restoreControls("floorEditor");
        this.collisionOverlayGraphics.destroy();
        this.gridGraphics.destroy();
        this.tilePreviewImage.destroy();
        this.scene.input.setDefaultCursor("auto");
    }

    public subscribeToGameMapFrontWrapperEvents(gameMapFrontWrapper: GameMapFrontWrapper): void {
        this.gameMapChangedSubscription = gameMapFrontWrapper.getMapChangedObservable().subscribe(() => {
            this.redrawCollisionOverlay();
            this.drawGrid();
        });
    }

    public handleKeyDownEvent(event: KeyboardEvent): void {
        // No keyboard handling for the pencil tool yet.
    }

    public handleIncomingCommandMessage(editMapCommandMessage: EditMapCommandMessage): Promise<void> {
        const commandId = editMapCommandMessage.id;
        switch (editMapCommandMessage.editMapMessage?.message?.$case) {
            case "putTilesMessage": {
                const data = editMapCommandMessage.editMapMessage.message.putTilesMessage;
                const tileChanges: TileChange[] = data.tiles.map((tile) => ({
                    layerId: tile.layerId,
                    x: tile.x,
                    y: tile.y,
                    oldTile: tile.oldTile,
                    newTile: tile.newTile,
                }));
                return this.mapEditorModeManager
                    .executeLocalCommand(
                        new PutTilesFrontCommand(
                            this.scene.getGameMap().getWamFile()!,
                            tileChanges,
                            commandId,
                            this.scene.getGameMapFrontWrapper(),
                        ),
                    )
                    .then(() => this.redrawCollisionOverlay());
            }
            case "resizeMapMessage": {
                const data = editMapCommandMessage.editMapMessage.message.resizeMapMessage;
                return this.mapEditorModeManager
                    .executeLocalCommand(
                        new ResizeMapFrontCommand(
                            this.scene.getGameMap().getWamFile()!,
                            data.width,
                            data.height,
                            commandId,
                            this.scene.getGameMapFrontWrapper(),
                        ),
                    )
                    .then(() => this.redrawCollisionOverlay());
            }
            case "createTileLayerMessage": {
                const data = editMapCommandMessage.editMapMessage.message.createTileLayerMessage;
                return this.mapEditorModeManager
                    .executeLocalCommand(
                        new CreateTileLayerFrontCommand(
                            this.scene.getGameMap().getWamFile()!,
                            data.layerId,
                            data.name,
                            data.index,
                            data.opacity,
                            data.visible,
                            commandId,
                            this.scene.getGameMapFrontWrapper(),
                        ),
                    )
                    .then(() => this.redrawCollisionOverlay());
            }
            case "deleteTileLayerMessage": {
                const data = editMapCommandMessage.editMapMessage.message.deleteTileLayerMessage;
                return this.mapEditorModeManager
                    .executeLocalCommand(
                        new DeleteTileLayerFrontCommand(
                            this.scene.getGameMap().getWamFile()!,
                            data.layerId,
                            commandId,
                            this.scene.getGameMapFrontWrapper(),
                        ),
                    )
                    .then(() => this.redrawCollisionOverlay());
            }
            case "moveTileLayerMessage": {
                const data = editMapCommandMessage.editMapMessage.message.moveTileLayerMessage;
                return this.mapEditorModeManager
                    .executeLocalCommand(
                        new MoveTileLayerFrontCommand(
                            this.scene.getGameMap().getWamFile()!,
                            data.layerId,
                            data.newIndex,
                            commandId,
                            this.scene.getGameMapFrontWrapper(),
                        ),
                    )
                    .then(() => this.redrawCollisionOverlay());
            }
            case "updateTileLayerMessage": {
                const data = editMapCommandMessage.editMapMessage.message.updateTileLayerMessage;
                const config: UpdateTileLayerConfig = {
                    ...(data.name !== undefined ? { name: data.name } : {}),
                    ...(data.opacity !== undefined ? { opacity: data.opacity } : {}),
                    ...(data.visible !== undefined ? { visible: data.visible } : {}),
                };
                return this.mapEditorModeManager.executeLocalCommand(
                    new UpdateTileLayerFrontCommand(
                        this.scene.getGameMap().getWamFile()!,
                        data.layerId,
                        config,
                        commandId,
                        this.scene.getGameMapFrontWrapper(),
                    ),
                );
            }
            default:
                return Promise.resolve();
        }
    }

    private bindEventHandlers(): void {
        this.pointerDownEventHandler = (pointer: Pointer) => this.handlePointerDownEvent(pointer);
        this.pointerMoveEventHandler = (pointer: Pointer) => this.handlePointerMoveEvent(pointer);
        this.pointerUpEventHandler = () => this.handlePointerUpEvent();

        this.scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.pointerDownEventHandler);
        this.scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.pointerMoveEventHandler);
        this.scene.input.on(Phaser.Input.Events.POINTER_UP, this.pointerUpEventHandler);
    }

    private unbindEventHandlers(): void {
        this.scene.input.off(Phaser.Input.Events.POINTER_DOWN, this.pointerDownEventHandler);
        this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.pointerMoveEventHandler);
        this.scene.input.off(Phaser.Input.Events.POINTER_UP, this.pointerUpEventHandler);
    }

    private bindFreeCameraHandlers(): void {
        this.scene.input.keyboard?.on("keydown", this.freeCameraKeyDownHandler);
        this.scene.input.keyboard?.on("keyup", this.freeCameraKeyUpHandler);
        this.scene.input.on("wheel", this.freeCameraWheelHandler);
    }

    private unbindFreeCameraHandlers(): void {
        this.scene.input.keyboard?.off("keydown", this.freeCameraKeyDownHandler);
        this.scene.input.keyboard?.off("keyup", this.freeCameraKeyUpHandler);
        this.scene.input.off("wheel", this.freeCameraWheelHandler);
    }

    private handlePointerDownEvent(pointer: Pointer): void {
        if (!this.active) {
            return;
        }
        this.painting = true;
        this.eraseMode = pointer.rightButtonDown();
        this.gestureChanges.clear();
        this.rectStart = undefined;
        this.handleBrushAtPointer(pointer);
    }

    private handlePointerMoveEvent(pointer: Pointer): void {
        if (!this.active) {
            return;
        }
        this.updateTilePreview(pointer);
        if (!this.painting) {
            return;
        }
        // Fill and eyedropper are single-click actions; dragging should not repeat them.
        const brush = get(mapEditorTileBrushToolStore);
        if (brush === "fill" || brush === "eyedropper") {
            return;
        }
        this.handleBrushAtPointer(pointer);
    }

    private handlePointerUpEvent(): void {
        if (!this.painting) {
            return;
        }
        this.painting = false;
        this.rectStart = undefined;
        this.flushGesture();
    }

    private handleBrushAtPointer(pointer: Pointer): void {
        const cell = this.getCellAtPointer(pointer);
        if (!cell) {
            return;
        }

        const brush = get(mapEditorTileBrushToolStore);

        if (brush === "eyedropper") {
            const gid = this.scene.getGameMapFrontWrapper().getTopmostTileGidAt(cell.x, cell.y);
            if (gid !== undefined) {
                mapEditorSelectedTileIndexStore.set(gid);
                // Switch back to the pencil so the user can immediately paint the picked tile.
                mapEditorTileBrushToolStore.set("pencil");
            }
            return;
        }

        const erasing = this.eraseMode || brush === "eraser";
        let tileIndex: number;
        if (brush === "collision") {
            if (erasing) {
                tileIndex = 0;
            } else {
                const collisionGid = this.scene.getGameMapFrontWrapper().getCollisionTileGid();
                if (collisionGid === undefined) {
                    return;
                }
                tileIndex = collisionGid;
            }
        } else {
            tileIndex = erasing ? 0 : this.getSelectedTileIndex();
        }

        switch (brush) {
            case "fill":
                this.applyFill(cell.layerId, cell.x, cell.y, tileIndex);
                break;
            case "rect":
                this.applyRect(cell.layerId, cell.x, cell.y, tileIndex);
                break;
            default:
                this.applyPencil(cell.layerId, cell.x, cell.y, tileIndex);
                break;
        }

        this.redrawCollisionOverlay();
    }

    private redrawCollisionOverlay(): void {
        const gameMapFrontWrapper = this.scene.getGameMapFrontWrapper();
        const collisionGrid = gameMapFrontWrapper.getCollisionGrid({ emitMapChangedEvent: false });
        const { width, height } = gameMapFrontWrapper.getTileDimensions();

        const graphics = this.collisionOverlayGraphics;
        graphics.clear();
        graphics.fillStyle(0xff3b30, 0.35);
        for (let y = 0; y < collisionGrid.length; y++) {
            for (let x = 0; x < collisionGrid[y].length; x++) {
                if (collisionGrid[y][x] === PathTileType.Collider) {
                    graphics.fillRect(x * width, y * height, width, height);
                }
            }
        }
    }

    private drawGrid(): void {
        const gameMapFrontWrapper = this.scene.getGameMapFrontWrapper();
        const graphics = this.gridGraphics;
        graphics.clear();

        if (!get(mapEditorShowGridStore)) {
            graphics.setVisible(false);
            return;
        }

        const map = gameMapFrontWrapper.getMap();
        const mapWidth = map.width ?? 0;
        const mapHeight = map.height ?? 0;
        const { width: tileWidth, height: tileHeight } = gameMapFrontWrapper.getTileDimensions();

        graphics.setVisible(true);
        graphics.lineStyle(1, 0xffffff, 0.18);
        for (let x = 0; x <= mapWidth; x++) {
            graphics.moveTo(x * tileWidth, 0);
            graphics.lineTo(x * tileWidth, mapHeight * tileHeight);
        }
        for (let y = 0; y <= mapHeight; y++) {
            graphics.moveTo(0, y * tileHeight);
            graphics.lineTo(mapWidth * tileWidth, y * tileHeight);
        }
        graphics.strokePath();
    }

    private updateTilePreview(pointer: Pointer): void {
        if (!get(mapEditorTilePreviewStore)) {
            this.tilePreviewImage.setVisible(false);
            return;
        }

        const brush = get(mapEditorTileBrushToolStore);
        if (brush === "eyedropper") {
            this.tilePreviewImage.setVisible(false);
            return;
        }
        const erasing = pointer.rightButtonDown() || brush === "eraser";
        let gid: number | undefined;
        if (erasing) {
            gid = undefined;
        } else if (brush === "collision") {
            gid = this.scene.getGameMapFrontWrapper().getCollisionTileGid();
        } else {
            gid = this.getSelectedTileIndex();
        }

        if (gid === undefined) {
            this.tilePreviewImage.setVisible(false);
            return;
        }

        const cell = this.getCellAtPointer(pointer);
        const info = cell ? this.getTilePreviewInfo(gid) : undefined;
        if (!cell || !info) {
            this.tilePreviewImage.setVisible(false);
            return;
        }

        const { width: tileWidth, height: tileHeight } = this.scene.getGameMapFrontWrapper().getTileDimensions();
        // Phaser renders a cropped image offset by the crop origin (uvSource.x/y), so subtract it to
        // align the preview exactly on the target tile.
        this.tilePreviewImage
            .setTexture(info.textureKey)
            .setCrop(info.x, info.y, info.width, info.height)
            .setScale(1)
            .setPosition(cell.x * tileWidth - info.x, cell.y * tileHeight - info.y)
            .setVisible(true);
    }

    private getTilePreviewInfo(
        gid: number,
    ): { textureKey: string; x: number; y: number; width: number; height: number } | undefined {
        for (const tileset of this.scene.getGameMapFrontWrapper().phaserMap.tilesets) {
            if (gid >= tileset.firstgid && gid < tileset.firstgid + tileset.total) {
                const texture = tileset.image;
                if (!texture) {
                    return undefined;
                }
                const index = gid - tileset.firstgid;
                const tileX = index % tileset.columns;
                const tileY = Math.floor(index / tileset.columns);
                return {
                    textureKey: texture.key,
                    x: tileset.tileMargin + tileX * (tileset.tileWidth + tileset.tileSpacing),
                    y: tileset.tileMargin + tileY * (tileset.tileHeight + tileset.tileSpacing),
                    width: tileset.tileWidth,
                    height: tileset.tileHeight,
                };
            }
        }
        return undefined;
    }

    private getCellAtPointer(pointer: Pointer): { layerId: number; x: number; y: number } | undefined {
        const layerId = this.getSelectedLayerId();
        if (layerId === undefined) {
            return undefined;
        }

        const gameMapFrontWrapper = this.scene.getGameMapFrontWrapper();
        const map = gameMapFrontWrapper.getMap();
        const { x, y } = gameMapFrontWrapper.getTileIndexAt(pointer.worldX, pointer.worldY);

        if (x < 0 || y < 0 || x >= (map.width ?? 0) || y >= (map.height ?? 0)) {
            return undefined;
        }
        return { layerId, x, y };
    }

    private applyPencil(layerId: number, x: number, y: number, tileIndex: number): void {
        const key = `${x},${y}`;
        if (this.gestureChanges.has(key)) {
            return;
        }

        const oldTile = this.getTileAt(layerId, x, y);
        if (oldTile === tileIndex) {
            return;
        }

        const change: TileChange = { layerId, x, y, oldTile, newTile: tileIndex };
        this.gestureChanges.set(key, change);

        // Optimistic visual update (model + Phaser layer).
        this.scene.getGameMapFrontWrapper().applyTileChanges([change]);
        this.scene.markDirty();
    }

    private applyFill(layerId: number, x: number, y: number, tileIndex: number): void {
        const gameMapFrontWrapper = this.scene.getGameMapFrontWrapper();
        const changes = TilemapEditsManager.floodFill(gameMapFrontWrapper.getMap(), layerId, x, y, tileIndex);
        if (changes.length === 0) {
            return;
        }

        for (const change of changes) {
            this.gestureChanges.set(`${change.x},${change.y}`, change);
        }
        gameMapFrontWrapper.applyTileChanges(changes);
        this.scene.markDirty();
    }

    private applyRect(layerId: number, x: number, y: number, tileIndex: number): void {
        if (!this.rectStart) {
            this.rectStart = { x, y };
        }
        const start = this.rectStart;

        const gameMapFrontWrapper = this.scene.getGameMapFrontWrapper();

        // Revert the previous rectangle preview so the model reflects the pre-gesture state.
        const previous = Array.from(this.gestureChanges.values());
        if (previous.length > 0) {
            gameMapFrontWrapper.applyTileChanges(TilemapEditsManager.invertTileChanges(previous));
            this.gestureChanges.clear();
        }

        const changes = TilemapEditsManager.fillRectangle(
            gameMapFrontWrapper.getMap(),
            layerId,
            start.x,
            start.y,
            x,
            y,
            tileIndex,
        );

        for (const change of changes) {
            this.gestureChanges.set(`${change.x},${change.y}`, change);
        }
        gameMapFrontWrapper.applyTileChanges(changes);
        this.scene.markDirty();
    }

    private flushGesture(): void {
        if (this.gestureChanges.size === 0) {
            return;
        }
        const changes = Array.from(this.gestureChanges.values());
        this.gestureChanges.clear();

        const wamFile = this.scene.getGameMap().getWamFile();
        if (!wamFile) {
            return;
        }

        this.mapEditorModeManager
            .executeCommand(new PutTilesFrontCommand(wamFile, changes, undefined, this.scene.getGameMapFrontWrapper()))
            .catch((e) => console.error(e));
    }

    private getTileAt(layerId: number, x: number, y: number): number {
        const flatLayer = this.scene
            .getGameMapFrontWrapper()
            .getFlatLayers()
            .find((layer) => layer.type === "tilelayer" && layer.id === layerId);
        if (!flatLayer || flatLayer.type !== "tilelayer" || typeof flatLayer.data === "string") {
            return 0;
        }
        return flatLayer.data[x + y * flatLayer.width] ?? 0;
    }

    private getSelectedLayerId(): number | undefined {
        const selected = get(mapEditorSelectedTileLayerIdStore);
        if (selected !== undefined) {
            return selected;
        }
        const firstTileLayer = this.scene
            .getGameMapFrontWrapper()
            .getFlatLayers()
            .find((layer) => layer.type === "tilelayer");
        if (!firstTileLayer || firstTileLayer.type !== "tilelayer") {
            return undefined;
        }
        return firstTileLayer.id;
    }

    private getSelectedTileIndex(): number {
        const selected = get(mapEditorSelectedTileIndexStore);
        if (selected !== undefined) {
            return selected;
        }
        const tileset = this.scene.getGameMapFrontWrapper().phaserMap.tilesets[0];
        return tileset?.firstgid ?? 1;
    }
}
